import type { ArtAsset } from "../art/validate";

// One icon family: every icon in the app is a 16 x 16 frame from apps/web/src/art/icons-*.art.json, drawn as
// crisp inline SVG (one <path> per colour). Inline SVG renders on the server and in tests, scales by whole
// pixels with CSS, and needs no canvas or image request.
const files = import.meta.glob("../art/icons-*.art.json", { eager: true, import: "default" }) as Record<string, ArtAsset>;

interface Drawn {
  readonly paths: ReadonlyArray<{ fill: string; d: string }>;
}

function draw(asset: ArtAsset, rows: readonly string[]): Drawn {
  const byColour = new Map<string, string>();
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const c = row.charAt(x);
      if (c === "." || !asset.palette[c]) {
        x++;
        continue;
      }
      let n = 1;
      while (row.charAt(x + n) === c) n++;
      const fill = asset.palette[c]!;
      byColour.set(fill, `${byColour.get(fill) ?? ""}M${x} ${y}h${n}v1h-${n}z`);
      x += n;
    }
  });
  return { paths: [...byColour].map(([fill, d]) => ({ fill, d })) };
}

const ICONS = new Map<string, Drawn>();
for (const asset of Object.values(files)) {
  for (const [name, rows] of Object.entries(asset.frames)) ICONS.set(name, draw(asset, rows));
}

export type IconName = string;
export const ICON_NAMES: readonly IconName[] = [...ICONS.keys()];

export function PixelIcon({ name, size = 16, label, className }: { name: IconName; size?: number; label?: string; className?: string }) {
  const icon = ICONS.get(name);
  if (!icon) throw new Error(`unknown icon "${name}"`);
  const a11y = label ? { role: "img" as const, "aria-label": label } : { "aria-hidden": true as const };
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} shapeRendering="crispEdges" data-icon={name} className={className} {...a11y}>
      {icon.paths.map((p) => (
        <path key={p.fill} fill={p.fill} d={p.d} />
      ))}
    </svg>
  );
}
